import { getFirebaseToken } from '../firebase';

/**
 * Service de gestion des données météorologiques pour l'agriculture
 * Utilise OpenWeatherMap API via proxy backend pour la sécurité
 * Cache les données météo pendant 30 minutes pour minimiser les appels API
 */
class FarmRoadWeatherService {
  constructor() {
    this.weatherCache = new Map();
    this.cacheExpiry = 30 * 60 * 1000; // 30 minutes
  }

  /**
   * Obtient l'URL du proxy backend pour les appels météo
   */
  _getBackendUrl() {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    return `${backendUrl}/api/weather`;
  }

  /**
   * Obtient la localisation GPS d'une ferme depuis le cache ou l'API
   */
  async _getFarmLocation(farmId) {
    // En production, cette fonction récupérerait les coordonnées depuis l'API
    // Pour l'instant, on suppose qu'elles sont passées en paramètre
    return null; // À implémenter si nécessaire
  }

  /**
   * Construit les headers avec token Firebase pour authentification
   */
  async _headers() {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = await getFirebaseToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('FarmRoadWeatherService: Firebase token unavailable for weather request');
    }
    return headers;
  }

  /**
   * Gestion standardisée des réponses HTTP
   */
  async _handleResponse(response) {
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Weather API error (${response.status}): ${errorText}`);
    }
    return response.json();
  }

  /**
   * Récupère les données météorologiques actuelles pour une ferme
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @returns {Promise<Object>} Données météo actuelles avec indices agricoles
   */
  async getWeatherForLocation({ farmId }) {
    // Vérifier le cache
    const cacheKey = `current_${farmId}`;
    const cached = this.weatherCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      console.log(`FarmRoadWeatherService: Using cached weather data for farm ${farmId}`);
      return cached.data;
    }

    try {
      const headers = await this._headers();
      const url = `${this._getBackendUrl()}/current?farmId=${farmId}`;

      console.log(`FarmRoadWeatherService: Fetching current weather for farm ${farmId}`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      // Extraire et aplatir les données du format API backend
      const raw = responseData.data || responseData;

      // Calculer le point de rosée (formule Magnus-Tetens)
      const temp = raw.current?.temperature || 0;
      const humidity = raw.current?.humidity || 0;
      const dewPoint = temp - ((100 - humidity) / 5);

      const weatherData = {
        // Données météo actuelles
        temperature: raw.current?.temperature,
        feelsLike: raw.current?.feelsLike,
        feels_like: raw.current?.feelsLike,
        humidity: raw.current?.humidity,
        pressure: raw.current?.pressure,
        windSpeed: raw.current?.windSpeed,
        wind_speed: raw.current?.windSpeed,
        windDirection: raw.current?.windDirection,
        wind_direction: raw.current?.windDirection,
        wind_gust: raw.current?.windSpeed, // Pas de données rafales dans API gratuite
        cloudiness: raw.current?.cloudiness,
        visibility: raw.current?.visibility,
        description: raw.current?.description,
        weather_description: raw.current?.description,
        icon: raw.current?.icon,
        weather_icon: raw.current?.icon,
        uvIndex: raw.current?.uvIndex,
        dew_point: dewPoint,

        // Précipitations
        rain1h: raw.precipitation?.rain1h || 0,
        rain3h: raw.precipitation?.rain3h || 0,
        snow1h: raw.precipitation?.snow1h || 0,
        precipitation_amount: (raw.precipitation?.rain1h || 0) + (raw.precipitation?.snow1h || 0),

        // Indices agricoles
        botrytis_risk: raw.agricultural?.botrytisRisk,
        botrytisRisk: raw.agricultural?.botrytisRisk,
        powdery_mildew_risk: raw.agricultural?.powderyMildewRisk,
        powderyMildewRisk: raw.agricultural?.powderyMildewRisk,
        evapotranspiration: raw.agricultural?.evapotranspiration,
        wind_stress: raw.agricultural?.windStressLevel,
        windStressLevel: raw.agricultural?.windStressLevel,

        // Métadonnées
        farmId: raw.farmId,
        timestamp: raw.timestamp,
        latitude: raw.location?.latitude,
        longitude: raw.location?.longitude
      };

      // Mettre en cache
      this.weatherCache.set(cacheKey, {
        data: weatherData,
        timestamp: Date.now()
      });

      return weatherData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error fetching current weather:', error);
      throw error;
    }
  }

  /**
   * Récupère les prévisions météorologiques (7 jours)
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @returns {Promise<Object>} Prévisions météo (hourly 48h + daily 7j)
   */
  async getWeatherForecast({ farmId }) {
    // Vérifier le cache
    const cacheKey = `forecast_${farmId}`;
    const cached = this.weatherCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      console.log(`FarmRoadWeatherService: Using cached forecast data for farm ${farmId}`);
      return cached.data;
    }

    try {
      const headers = await this._headers();
      const url = `${this._getBackendUrl()}/forecast?farmId=${farmId}`;

      console.log(`FarmRoadWeatherService: Fetching weather forecast for farm ${farmId}`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      // Extraire les données du format API backend
      const forecastData = responseData.data || responseData;

      // Mettre en cache
      this.weatherCache.set(cacheKey, {
        data: forecastData,
        timestamp: Date.now()
      });

      return forecastData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error fetching forecast:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique météo quotidien agrégé pour une période donnée
   * Lit depuis weather_history_daily (données agrégées quotidiennes)
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @param {string} params.startTime - Date de début (YYYY-MM-DD)
   * @param {string} params.endTime - Date de fin (YYYY-MM-DD)
   * @param {number} params.limit - Nombre max de résultats (défaut: 365)
   * @returns {Promise<Array>} Historique météo quotidien
   */
  async getWeatherHistory({ farmId, startTime, endTime, limit = 365 }) {
    try {
      const headers = await this._headers();

      // Utiliser /history/daily pour récupérer les données agrégées quotidiennes
      // depuis weather_history_daily au lieu des observations horaires depuis weather_data
      const url = `${this._getBackendUrl()}/history/daily?farmId=${farmId}&startDate=${startTime}&endDate=${endTime}&limit=${limit}`;

      console.log(`[FarmRoadWeatherService] Fetching daily weather history for farm ${farmId} (${startTime} → ${endTime})`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      // L'endpoint /history/daily retourne { success, data: { daily: [...] } }
      // Retourner directement le tableau 'daily'
      return responseData.data?.daily || [];
    } catch (error) {
      console.error('[FarmRoadWeatherService] Error fetching daily weather history:', error);
      throw error;
    }
  }

  /**
   * Rafraîchit immédiatement les données météo pour une ferme
   * @param {string} farmId - ID de la ferme
   * @returns {Promise<Object>} Données météo rafraîchies
   */
  async refreshWeatherData(farmId) {
    try {
      const headers = await this._headers();
      const url = `${this._getBackendUrl()}/refresh?farmId=${farmId}`;

      console.log(`FarmRoadWeatherService: Refreshing weather data for farm ${farmId}`);

      const response = await fetch(url, {
        method: 'POST',
        headers
      });
      const responseData = await this._handleResponse(response);

      // Invalider le cache
      this.weatherCache.delete(`current_${farmId}`);
      this.weatherCache.delete(`forecast_${farmId}`);

      return responseData.data || responseData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error refreshing weather data:', error);
      throw error;
    }
  }

  /**
   * Calcule les indices agricoles critiques basés sur la météo
   * @param {Object} weatherData - Données météo actuelles
   * @returns {Object} Indices agricoles calculés
   */
  calculateAgriculturalIndices(weatherData) {
    if (!weatherData || !weatherData.current) {
      return null;
    }

    const { temperature, humidity, windSpeed, pressure } = weatherData.current;

    return {
      // Risque de Botrytis (moisissure grise) - 0 à 100
      botrytisRisk: this._calculateBotrytisRisk(temperature, humidity),

      // Risque d'Oïdium (blanc) - 0 à 100
      powderyMildewRisk: this._calculatePowderyMildewRisk(temperature, humidity),

      // Estimation de l'évapotranspiration (mm/jour)
      evapotranspirationEstimate: this._estimateET(temperature, humidity, windSpeed),

      // Niveau de stress éolien
      windStressLevel: this._calculateWindStress(windSpeed),

      // Pression atmosphérique (contexte)
      atmosphericPressure: pressure,

      // Gradient potentiel serre-extérieur (pour contexte)
      temperatureContext: this._getTemperatureContext(temperature)
    };
  }

  /**
   * Calcule le risque de Botrytis (moisissure grise)
   * Conditions favorables: T° 15-25°C, Humidité > 85%
   */
  _calculateBotrytisRisk(temperature, humidity) {
    let risk = 0;

    // Température optimale pour Botrytis: 15-25°C
    if (temperature >= 15 && temperature <= 25) {
      risk += 50;
      // Pic autour de 20°C
      if (temperature >= 18 && temperature <= 22) {
        risk += 10;
      }
    } else if (temperature >= 10 && temperature <= 30) {
      risk += 20;
    }

    // Humidité critique
    if (humidity > 85) {
      risk += 40;
    } else if (humidity > 75) {
      risk += 25;
    } else if (humidity > 65) {
      risk += 10;
    }

    return Math.min(100, risk);
  }

  /**
   * Calcule le risque d'Oïdium (blanc)
   * Conditions favorables: T° 20-30°C, Humidité 40-70%
   */
  _calculatePowderyMildewRisk(temperature, humidity) {
    let risk = 0;

    // Température optimale pour Oïdium: 20-30°C
    if (temperature >= 20 && temperature <= 30) {
      risk += 50;
      // Pic autour de 25°C
      if (temperature >= 23 && temperature <= 27) {
        risk += 10;
      }
    } else if (temperature >= 15 && temperature <= 35) {
      risk += 20;
    }

    // Humidité modérée (paradoxalement, Oïdium n'aime pas l'excès d'humidité)
    if (humidity >= 40 && humidity <= 70) {
      risk += 40;
    } else if (humidity >= 30 && humidity <= 80) {
      risk += 20;
    }

    return Math.min(100, risk);
  }

  /**
   * Estime l'évapotranspiration de référence (ET0) - méthode simplifiée
   * Formule simplifiée basée sur température, humidité et vent
   */
  _estimateET(temperature, humidity, windSpeed) {
    // Formule simplifiée Penman-Monteith adaptée
    // ET0 (mm/jour) approximative

    // Déficit de pression de vapeur (VPD)
    const saturationVP = 0.6108 * Math.exp((17.27 * temperature) / (temperature + 237.3));
    const actualVP = saturationVP * (humidity / 100);
    const vpd = saturationVP - actualVP;

    // Contribution de la température (radiation solaire simplifiée)
    const radiationEffect = Math.max(0, temperature - 5) * 0.15;

    // Contribution du vent
    const windEffect = windSpeed * 0.3;

    // Contribution du VPD
    const vpdEffect = vpd * 2.5;

    // ET0 estimée
    const et0 = radiationEffect + windEffect + vpdEffect;

    return Math.max(0, Math.round(et0 * 10) / 10); // Arrondi à 1 décimale
  }

  /**
   * Calcule le niveau de stress éolien
   */
  _calculateWindStress(windSpeed) {
    if (windSpeed < 2) return 'TRÈS FAIBLE';
    if (windSpeed < 5) return 'FAIBLE';
    if (windSpeed < 8) return 'MODÉRÉ';
    if (windSpeed < 11) return 'ÉLEVÉ';
    return 'TRÈS ÉLEVÉ (RISQUE)';
  }

  /**
   * Fournit un contexte sur la température extérieure
   */
  _getTemperatureContext(temperature) {
    if (temperature < 5) return 'TRÈS FROID - Risque gel';
    if (temperature < 15) return 'FROID - Chauffage serre nécessaire';
    if (temperature < 25) return 'OPTIMAL - Conditions favorables';
    if (temperature < 35) return 'CHAUD - Refroidissement serre nécessaire';
    return 'TRÈS CHAUD - Stress thermique sévère';
  }

  /**
   * Récupère l'historique météo quotidien agrégé
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @param {string} [params.startDate] - Date de début (YYYY-MM-DD)
   * @param {string} [params.endDate] - Date de fin (YYYY-MM-DD)
   * @param {number} [params.days] - Nombre de jours (alternative à startDate/endDate)
   * @param {number} [params.limit] - Nombre maximum de résultats
   * @returns {Promise<Object>} Historique quotidien
   */
  async getDailyWeatherHistory({ farmId, startDate, endDate, days, limit }) {
    try {
      const headers = await this._headers();
      const params = new URLSearchParams({ farmId });

      if (days) {
        params.append('days', days);
      } else {
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
      }
      if (limit) params.append('limit', limit);

      const url = `${this._getBackendUrl()}/history/daily?${params.toString()}`;

      console.log(`FarmRoadWeatherService: Fetching daily weather history for farm ${farmId}`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      return responseData.data || responseData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error fetching daily weather history:', error);
      throw error;
    }
  }

  /**
   * Récupère un résumé de l'historique météo
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @param {number} [params.days=7] - Nombre de jours
   * @returns {Promise<Object>} Résumé de l'historique
   */
  async getWeatherHistorySummary({ farmId, days = 7 }) {
    try {
      const headers = await this._headers();
      const url = `${this._getBackendUrl()}/history/summary?farmId=${farmId}&days=${days}`;

      console.log(`FarmRoadWeatherService: Fetching weather history summary for farm ${farmId}`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      return responseData.data || responseData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error fetching weather history summary:', error);
      throw error;
    }
  }

  /**
   * Calcule le bilan hydrique pour une période
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.farmId - ID de la ferme
   * @param {string} [params.startDate] - Date de début (YYYY-MM-DD)
   * @param {string} [params.endDate] - Date de fin (YYYY-MM-DD)
   * @param {number} [params.days] - Nombre de jours (alternative)
   * @returns {Promise<Object>} Bilan hydrique
   */
  async getWaterBalance({ farmId, startDate, endDate, days }) {
    try {
      const headers = await this._headers();
      const params = new URLSearchParams({ farmId });

      if (days) {
        params.append('days', days);
      } else {
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
      }

      const url = `${this._getBackendUrl()}/history/water-balance?${params.toString()}`;

      console.log(`FarmRoadWeatherService: Fetching water balance for farm ${farmId}`);

      const response = await fetch(url, { headers });
      const responseData = await this._handleResponse(response);

      return responseData.data || responseData;
    } catch (error) {
      console.error('FarmRoadWeatherService: Error fetching water balance:', error);
      throw error;
    }
  }

  /**
   * Vide le cache météo (pour tests ou rafraîchissement forcé)
   */
  clearCache() {
    console.log('FarmRoadWeatherService: Clearing weather cache');
    this.weatherCache.clear();
  }

  /**
   * Nettoie les entrées de cache expirées
   */
  cleanExpiredCache() {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, value] of this.weatherCache.entries()) {
      if (now - value.timestamp >= this.cacheExpiry) {
        this.weatherCache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`FarmRoadWeatherService: Cleaned ${cleanedCount} expired cache entries`);
    }
  }
}

export default new FarmRoadWeatherService();
